import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  Typography,
  Form,
  Input,
  Button,
  Select,
  useToast,
} from '@crisellevaldez/speculo-ui';
import { Organization } from '../../../types/organization.types';
import { useUpdateOrganizationSettings } from '../../../hooks/organizations/useUpdateOrganizationSettings';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { RotateCw } from 'lucide-react';
import { TwilioFormData, TwilioPhoneNumber } from '../../../types/twilio.types';
import { useTwilioOperations } from '../../../hooks/twilio/useTwilioOperations';
import { setupBlandInboundNumber } from '../../../api/bland';
import { saveTwilioCredentials } from '../../../api/twilioCredentials';
import {
  describeSmsActivation,
  twilioCredentialsErrorMessage,
  type SmsActivationCopy,
} from '../../../utils/twilioCredentialsCopy';

interface TwilioModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg?: Organization;
  onSuccess?: () => void;
}

const twilioSchema = z.object({
  account_sid: z.string().min(1, 'Account SID is required'),
  auth_token: z.string().min(1, 'Auth token is required'),
  phone_number: z.string().min(1, 'Phone number is required'),
  encrypted_key: z.string().min(1, 'Key is required'),
});

export const TwilioModal: React.FC<TwilioModalProps> = ({
  isOpen,
  onClose,
  selectedOrg,
  onSuccess,
}) => {
  const { showToast } = useToast();
  const { updateSettings } = useUpdateOrganizationSettings();
  const [phoneNumbers, setPhoneNumbers] = useState<TwilioPhoneNumber[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { fetchPhoneNumbers, generateKey, isGeneratingKey, isFetchingNumbers } =
    useTwilioOperations();

  const form = useForm<TwilioFormData>({
    resolver: zodResolver(twilioSchema),
    defaultValues: {
      account_sid: '',
      auth_token: '',
      phone_number: '',
      encrypted_key: '',
    },
  });

  const { handleSubmit, watch, setValue, reset, register } = form;
  const accountSid = watch('account_sid');
  const authToken = watch('auth_token');

  // Tracks the SID/Token pair we've already auto-fetched for, so we don't
  // refetch on every keystroke once numbers are loaded.
  const lastAutoFetchedRef = useRef<string>('');

  // Reset to blank on open / org switch. We intentionally don't prefill from
  // Firestore — ops pastes SID/Token/Key from the legacy-creds CSV.
  useEffect(() => {
    if (isOpen && selectedOrg) {
      reset({
        account_sid: '',
        auth_token: '',
        phone_number: '',
        encrypted_key: '',
      });

      setPhoneNumbers([]);
      lastAutoFetchedRef.current = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedOrg?.id]);

  const handleFetchNumbers = async (sid: string, token: string) => {
    const numbers = await fetchPhoneNumbers(sid, token, selectedOrg?.id);
    setPhoneNumbers(numbers);

    // A number picked under a previous SID/Token must not survive a lookup
    // that no longer returns it: Save requires a number, so this keeps
    // credentials Twilio rejects from reaching the save.
    const selected = form.getValues('phone_number');
    if (selected && !numbers.some((n) => n.phoneNumber === selected)) {
      setValue('phone_number', '');
    }

    // Auto-select the org's currently-configured outbound number if Twilio
    // returned it. Compare on digits-only so +1XXXXXXXXXX matches XXXXXXXXXX.
    const currentOutbound = selectedOrg?.phoneNumbers?.outbound;
    if (currentOutbound && numbers.length > 0) {
      const currentDigits = currentOutbound.replace(/\D/g, '');
      const match = numbers.find(
        (n) => n.phoneNumber.replace(/\D/g, '') === currentDigits,
      );
      if (match) {
        setValue('phone_number', match.phoneNumber);
      }
    }

    // Only cache the (SID, Token) pair when Twilio actually returned numbers.
    // Caching on empty/failed results would block legit retries after a
    // rate-limit or transient error.
    if (numbers.length > 0) {
      lastAutoFetchedRef.current = `${sid}|${token}`;
    } else {
      lastAutoFetchedRef.current = '';
    }
  };

  // Auto-fetch phone numbers once SID + Token look fully pasted in.
  // Twilio SIDs are 34 chars and auth tokens are 32 hex chars, so a >=30
  // length gate cheaply distinguishes a paste from mid-typing.
  useEffect(() => {
    if (!isOpen || !selectedOrg) return;
    if (accountSid.length < 30 || authToken.length < 30) return;

    const pairKey = `${accountSid}|${authToken}`;
    if (lastAutoFetchedRef.current === pairKey) return;
    if (isFetchingNumbers || isGeneratingKey) return;

    const timer = setTimeout(() => {
      handleFetchNumbers(accountSid, authToken);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedOrg?.id, accountSid, authToken]);

  const handleGenerateKey = async () => {
    const key = await generateKey(accountSid, authToken, selectedOrg?.id);
    if (key) {
      form.reset({
        ...form.getValues(),
        encrypted_key: key,
      });
      // Refresh phone numbers after generating new key
      await handleFetchNumbers(accountSid, authToken);
    }
  };

  const onSubmit = async (data: TwilioFormData) => {
    if (!selectedOrg) return;

    setIsSubmitting(true);
    try {
      // First, update Firestore with Twilio settings
      await updateSettings({
        id: selectedOrg.id,
        data: {
          twilioSecretRef: data.encrypted_key,
          phoneNumbers: {
            outbound: data.phone_number,
            inbound: data.phone_number,
            signCall: selectedOrg.phoneNumbers?.signCall || '',
          },
        },
      });

      // Runs after updateSettings because remi-api matches the Messaging
      // Service against the outbound number saved there. The raw error is
      // never logged: its axios config.data carries the auth token.
      let smsActivation: SmsActivationCopy;
      try {
        const saved = await saveTwilioCredentials(
          selectedOrg.id,
          data.account_sid,
          data.auth_token
        );
        smsActivation = describeSmsActivation(saved.sms_activation);
      } catch (credentialsError) {
        showToast({ message: twilioCredentialsErrorMessage(credentialsError), variant: 'error' });
        return;
      }
      setValue('auth_token', '');
      lastAutoFetchedRef.current = '';

      // Then, configure the inbound number with Bland.ai
      try {
        await setupBlandInboundNumber(data.phone_number, selectedOrg.id, data.encrypted_key);
        showToast({
          message: `Twilio settings and inbound number configured. ${smsActivation.message}`,
          variant: smsActivation.activated ? 'success' : 'warning',
        });
      } catch (blandError) {
        console.error('Error configuring Bland inbound number:', blandError);
        // Show warning but don't block the flow since Firestore update succeeded
        showToast({
          message: `Twilio settings saved, but inbound configuration may need attention. ${smsActivation.message}`,
          variant: 'warning',
        });
      }

      onSuccess?.();
    } catch (error) {
      console.error('Error updating Twilio settings:', error);
      showToast({
        message: 'Failed to update Twilio settings',
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header>
        <Typography variant="h3">Twilio</Typography>
      </Modal.Header>
      <Modal.Body>
        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Control required>
                <Form.Label>SID</Form.Label>
                <Input
                  {...register('account_sid')}
                  placeholder="Enter Twilio Account SID"
                  error={form.formState.errors.account_sid?.message}
                />
              </Form.Control>
              <Form.Control required>
                <Form.Label>Token</Form.Label>
                <div className="flex gap-2">
                  <div className="flex-1 min-w-0">
                    <Input
                      {...register('auth_token')}
                      type="password"
                      placeholder="Enter Twilio Auth Token"
                      error={form.formState.errors.auth_token?.message}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleGenerateKey}
                    disabled={!accountSid || !authToken || isGeneratingKey}
                    isLoading={isGeneratingKey}
                    className="shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap"
                  >
                    <RotateCw className={`h-4 w-4 ${isGeneratingKey ? 'animate-spin' : ''}`} />
                    {isGeneratingKey ? 'Generating' : 'Generate Key'}
                  </Button>
                </div>
              </Form.Control>
            </div>

            <Form.Control required>
              <Form.Label>Key</Form.Label>
              <Input
                {...register('encrypted_key')}
                placeholder={
                  isGeneratingKey
                    ? 'Generating key…'
                    : 'Paste existing key or click Generate Key'
                }
                error={form.formState.errors.encrypted_key?.message}
              />
            </Form.Control>

            <Form.Control required>
              <Form.Label>Phone Number</Form.Label>
              <div className="flex gap-2">
                <div className="flex-1 min-w-0">
                  <Select
                    value={watch('phone_number')}
                    onChange={(val) => setValue('phone_number', (val as string) || '')}
                    error={form.formState.errors.phone_number?.message}
                    options={phoneNumbers.map((num) => ({
                      value: num.phoneNumber,
                      label: `${num.friendlyName} - ${num.phoneNumber}`,
                    }))}
                    placeholder="Select phone number"
                    isLoading={isFetchingNumbers}
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleFetchNumbers(accountSid, authToken)}
                  disabled={!accountSid || !authToken || isFetchingNumbers}
                  isLoading={isFetchingNumbers}
                  className="shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap"
                  title="Refresh phone numbers from Twilio"
                >
                  <RotateCw className={`h-4 w-4 ${isFetchingNumbers ? 'animate-spin' : ''}`} />
                  {isFetchingNumbers ? 'Loading' : 'Refresh'}
                </Button>
              </div>
            </Form.Control>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} type="button">
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit(onSubmit)}
          isLoading={isSubmitting}
          disabled={isSubmitting || isFetchingNumbers || isGeneratingKey}
        >
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
